import React, { useEffect, useRef, useState } from 'react';

type DeferredSrcImageProps = React.ImgHTMLAttributes<HTMLImageElement>;

/**
 * Native loading="lazy" still requested every /services grid image at t≈0 in a
 * Chrome waterfall (distance heuristic ~1250px plus LHCI/headless treating
 * below-fold as fetchable). Omit src until the node is ~80px from view.
 */
const DeferredSrcImage: React.FC<DeferredSrcImageProps> = ({
  src,
  alt = '',
  loading = 'lazy',
  decoding = 'async',
  ...rest
}) => {
  const ref = useRef<HTMLImageElement>(null);
  const [activeSrc, setActiveSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!el || !src) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setActiveSrc(src);
          io.disconnect();
        }
      },
      { rootMargin: '80px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [src]);

  return (
    <img
      ref={ref}
      {...rest}
      alt={alt}
      src={activeSrc}
      loading={loading}
      decoding={decoding}
    />
  );
};

export default DeferredSrcImage;
